import { Link } from "react-router-dom";
import { Loader2, CheckCircle } from "lucide-react";
import { useNewsletterSubscribe } from "@/hooks/useNewsletterSubscribe";
import NewsletterHoneypot from "@/components/NewsletterHoneypot";
import { OutspokenWordmark } from "@/components/OutspokenLogo";
import MandalaBg from "@/components/MandalaBg";

function CompactNewsletter() {
  const { email, setEmail, honeypot, setHoneypot, loading, done, handleSubmit } = useNewsletterSubscribe();

  if (done) {
    return (
      <div className="flex items-center justify-center gap-2 text-accent py-2" style={{ fontSize: "clamp(0.65rem, 1vw, 0.75rem)" }}>
        <CheckCircle size={14} /> <span>Subscribed!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-sm mx-auto">
      <NewsletterHoneypot value={honeypot} onChange={setHoneypot} />
      <label htmlFor="final-cta-email" className="block mb-1.5" style={{ fontSize: "13px", color: "hsl(var(--manuscript) / 0.7)" }}>
        Your email
      </label>
      <div className="flex gap-2">
        <input id="final-cta-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
          className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent/50 min-h-[36px]" style={{ fontSize: "clamp(0.65rem, 1vw, 0.75rem)" }} />
        <button type="submit" disabled={loading}
          className="bg-accent text-accent-foreground px-4 py-2 rounded-lg font-semibold hover:bg-accent/90 disabled:opacity-50 min-h-[36px] flex items-center gap-1 shrink-0" style={{ fontSize: "clamp(0.65rem, 1vw, 0.75rem)" }}>
          {loading ? <Loader2 size={12} className="animate-spin" /> : "Get the Newsletter"}
        </button>
      </div>
    </form>
  );
}

export default function FinalCTAScene() {
  return (
    <section className="home-band flex flex-col items-center px-5 sm:px-8 relative overflow-hidden texture-grain">
      <MandalaBg opacity={0.05} withOverlay={false} />

      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 45% at 50% 100%, hsl(var(--marigold) / 0.10), transparent 70%)",
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
        <div
          className="scroll-reveal h-px w-20 origin-center mb-8"
          style={{ background: "hsl(var(--marigold) / 0.55)" }}
        />

        <h2
          className="scroll-reveal font-display text-manuscript leading-[1.02]"
          style={{
            fontSize: "clamp(2.2rem, 6vw, 5.2rem)",
            letterSpacing: "-0.02em",
            fontWeight: 500,
          }}
        >
          Bring the decision.{" "}
          <span className="italic" style={{ color: "hsl(var(--marigold))" }}>
            We'll help you think it through.
          </span>
        </h2>

        <p
          className="scroll-reveal mt-6 font-display italic text-manuscript/55 max-w-xl"
          style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.1rem)" }}
        >
          One question. One clear next step. Private and yours.
        </p>

        <div
          className="scroll-reveal"
          style={{ marginTop: "clamp(2rem, 4vh, 2.75rem)" }}
        >
          <Link id="final-primary-cta" to="/coach" className="btn-primary">
            Start a Session
          </Link>
        </div>

        <div className="w-full scroll-reveal" style={{ marginTop: "clamp(2rem, 4vh, 3rem)" }}>
          <p
            className="section-eyebrow mb-3"
            style={{ fontSize: "clamp(9px, 0.85vw, 11px)" }}
          >
            Get one clear thought a week, written by The Council
          </p>
          <CompactNewsletter />
        </div>

      </div>

      {/* GEO: Visually-hidden crawlable context for AI engines */}
      <section className="geo-visible" aria-hidden="false">
        <p>
          Outspoken is India's Dharmic Intelligence Platform — ancient decision science for modern professionals. It applies the structured decision frameworks of the Bhagavad Gita, Arthashastra, Ramayan, and Mahabharata to modern high-stakes decisions in career, relationships, strategy, and life direction.
        </p>
      </section>

      {/* Compact footer */}
      <div className="relative z-10 w-full mt-auto border-t border-border/30 py-3 sm:py-4 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-4 text-muted-foreground" style={{ fontSize: "clamp(0.55rem, 0.8vw, 0.625rem)" }}>
            <Link to="/" className="text-foreground"><OutspokenWordmark height={18} className="text-foreground" /></Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </div>
          <p className="text-muted-foreground/60" style={{ fontSize: "clamp(0.55rem, 0.8vw, 0.625rem)" }}>© 2026 Outspoken</p>
        </div>
      </div>
    </section>
  );
}
